/**
 * Scanning a question's site tag (ADR-0034): the rear camera reads the QR
 * code, or the phone reads the NFC tag. Only text that looks like one of our
 * tags is accepted here; the server checks the signature and the site.
 */
import { useEffect, useRef, useState } from 'react';
import { Banner, Button, Dialog, Segmented } from '../../components';
import { useI18n } from '../../lib/i18n';
import { isTagPayload, nfcAvailable, readNfcText, scanQr } from '../../lib/native/tags';

export type TagMethod = 'QR' | 'NFC';

export function TagScanner({ open, onClose, onScan, title }: { open: boolean; onClose: () => void; onScan: (scan: { payload: string; method: TagMethod }) => void; title?: string }) {
  const { t } = useI18n();
  const [method, setMethod] = useState<TagMethod>('QR');
  const [nfc, setNfc] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hint, setHint] = useState<string | null>(null);
  const video = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (!open) return;
    let live = true;
    nfcAvailable().then((ok) => live && setNfc(ok), () => {});
    return () => {
      live = false;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setHint(null);
    const ctl = new AbortController();
    const run = async () => {
      if (method === 'QR') {
        if (!video.current) return;
        const payload = await scanQr(video.current, ctl.signal, () => setHint(t('ui.capture.tag.notOurs')));
        return { payload, method };
      }
      for (;;) {
        const text = await readNfcText(ctl.signal);
        if (isTagPayload(text)) return { payload: text, method };
        setHint(t('ui.capture.tag.notTag'));
      }
    };
    run().then(
      (r) => {
        if (r && !ctl.signal.aborted) onScan(r);
      },
      (e: unknown) => {
        if (ctl.signal.aborted || (e instanceof DOMException && e.name === 'AbortError')) return;
        setError(method === 'QR' ? t('ui.capture.tag.cameraError') : t('ui.capture.tag.nfcError'));
      },
    );
    return () => ctl.abort();
  }, [open, method, onScan, t]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={title ?? t('ui.capture.tag.title')}
      actions={
        <Button variant="secondary" onClick={onClose}>
          {t('common.cancel')}
        </Button>
      }
    >
      <div className="flex flex-col gap-3">
        {nfc && (
          <Segmented<TagMethod>
            label={t('ui.capture.tag.title')}
            value={method}
            onChange={(v) => v && setMethod(v)}
            options={[
              { value: 'QR', label: t('ui.capture.tag.qr') },
              { value: 'NFC', label: t('ui.capture.tag.nfc') },
            ]}
          />
        )}
        {method === 'QR' ? (
          <>
            <video ref={video} muted playsInline className="aspect-square w-full rounded-xl bg-black object-cover" aria-label={t('ui.capture.tag.qrHint')} />
            <p>{t('ui.capture.tag.qrHint')}</p>
          </>
        ) : (
          <p>{t('ui.capture.tag.nfcHint')}</p>
        )}
        {hint && !error && <p className="text-warning">{hint}</p>}
        {error && <Banner tone="danger">{error}</Banner>}
      </div>
    </Dialog>
  );
}
